<template>
    <div class="calendar-events-chart" :class="{ fullscreen: fullScreen }">
        <div class="title" v-if="fullScreen">
            <ChartDoddleIcon />
            <h2>{{ $t("report.calendarEvents") }}</h2>
        </div>
        <div class="toolbar">
            <div class="left">
                <div class="buttons">
                    <IconButton
                        :classNames="tableMode ? '' : 'selected'"
                        :onClick="() => (tableMode = false)"
                        v-tippy="{ content: $t('report.chartView') }"
                    >
                        <md-icon>insights</md-icon>
                    </IconButton>
                    <IconButton
                        :classNames="tableMode ? 'selected' : ''"
                        :onClick="() => (tableMode = true)"
                        v-tippy="{ content: $t('report.tableView') }"
                    >
                        <md-icon>table_view</md-icon>
                    </IconButton>
                    <IconButton :onClick="exportToExcel" v-tippy="{ content: $t('report.download') }">
                        <md-icon>download</md-icon>
                    </IconButton>
                    <IconButton :onClick="switchToFullScreenMode" v-tippy="{ content: $t('report.fullScreen') }">
                        <md-icon v-if="!fullScreen">fullscreen</md-icon>
                        <md-icon v-else>fullscreen_exit</md-icon>
                    </IconButton>
                </div>
            </div>
        </div>
        <EChartsLine
            :labels="getXAxisData()"
            :datasets="getChartDatasets()"
            :height="320"
            v-if="!tableMode"
        />
        <TableView :cols="getCols()" :rows="getRows()" v-if="tableMode" />
        <div class="stats">
            <StatCard
                :title="getTitle('average')"
                :value="formatStat(stats.AverageCalendarEventCount).value"
                :unit="formatStat(stats.AverageCalendarEventCount).unit"
            />
            <StatCard
                :title="getTitle('max')"
                :value="formatStat(stats.MaxCalendarEventCount).value"
                :unit="formatStat(stats.MaxCalendarEventCount).unit"
            />
            <StatCard
                :title="getTitle('min')"
                :value="formatStat(stats.MinCalendarEventCount).value"
                :unit="formatStat(stats.MinCalendarEventCount).unit"
            />
        </div>
    </div>
</template>
<script>
import Vue from "vue";
import moment from "moment";
import _ from "lodash";
import IconButton from "../../Pages/Settings/components/IconButton.vue";
import EChartsLine from "../../../components/charts/EChartsLine.vue";
import StatCard from "../../Pages/Chatbot/pages/reports/charts/StatCard.vue";
import TableView from "../../Pages/Chatbot/pages/reports/charts/TableView.vue";
import { expertToExcel } from "../../../utils/utils";
import { dateToHourFromSecondsShortFormat } from "../../../utils/filters";
import { REPORT_API } from "../../../api/statistics";
import log2 from "../../../utils/log2";
import ChartDoddleIcon from "../common/icons/ChartDoddleIcon.vue";
import fscreen from "fscreen";

const log = log2("CalendarEventsChart");
export default Vue.extend({
    name: "CalendarEventsChart",
    components: { ChartDoddleIcon, TableView, IconButton, StatCard, EChartsLine },
    props: {
        filters: {
            type: Object,
            default: () => ({
                users: [],
                channels: [],
                startDate: moment().startOf("day").toISOString(),
                endDate: moment().endOf("day").toISOString(),
            }),
        },
    },
    data() {
        return {
            stats: {
                TotalCalendarEventCount: 0,
                AverageCalendarEventCount: 0,
                MaxCalendarEventCount: 0,
                MinCalendarEventCount: 0,
            },
            tableMode: false,
            loading: true,
            loadingInstance: null,
            graphData: {
                buckets: [],
                timeSeriesType: "hour",
            },
            fullScreen: false,
            cancels: [],
        };
    },
    methods: {
        getTitle(type) {
            const hourlyText = this.$t("report.hourly");
            if (this.getStats().isHourlyAverage) {
                return `${this.$t(`report.${type}`)} ${hourlyText}`;
            }
            return this.$t(`report.${type}`);
        },
        formatStat(value) {
            if (value === 0) {
                return {
                    value: this.$t("report.noEnoughtData"),
                    unit: "",
                };
            }
            return {
                value: value,
                unit: this.$t("report.event"),
            };
        },

        switchToFullScreenMode() {
            if (this.fullScreen) {
                this.closeFullScreenMode();
            } else {
                const elem = document.querySelector(".calendar-events-chart");
                fscreen.requestFullscreen(elem);
                this.fullScreen = true;
            }
        },
        closeFullScreenMode() {
            fscreen.exitFullscreen();
        },
        fetch() {
            this.loading = true;
            this.loadingInstance.show();
            REPORT_API.calendarEvents(this.filters)
                .then((res) => {
                    log("calendarEvents data fetched", res);
                    this.graphData = res;
                })
                .finally(() => {
                    this.loading = false;
                });
        },
        humanizeDuration(totalSeconds) {
            return dateToHourFromSecondsShortFormat(totalSeconds, this.$i18n.locale, true);
            // return `${hours}h ${minutes}m ${seconds}s`;
        },
        getCols() {
            return [
                {
                    title: this.$t("chatbotv2.date"),
                    dataIndex: "date",
                    key: "date",
                    // scopedSlots: { customRender: "date" },
                },
                {
                    title: this.$t("report.eventCount"),
                    dataIndex: "eventCount",
                    key: "eventCount",
                    // scopedSlots: { customRender: "eventCount" },
                },
            ];
        },
        getRows() {
            const { buckets, timeSeriesType } = this.graphData;
            log("getRows.buckets", buckets);
            const rows = buckets.map((bucket) => {
                const eventCount = bucket.eventCount;
                const date = {
                    hour: moment(bucket.date).format("DD/MM/YYYY HH:mm"),
                    day: moment(bucket.date).format("DD/MM/YYYY"),
                    month: moment(bucket.date).format("MM/YYYY"),
                    week: moment(bucket.date).format("YYYY-WW[week]"),
                }[timeSeriesType];
                return {
                    date,
                    eventCount,
                };
            });
            return rows;
        },
        getStats() {
            const startDate = moment(this.filters.startDate);
            const endDate = moment(this.filters.endDate);
            const buckets = this.graphData?.buckets || [];
            log("getStats.buckets", buckets);
            const CalendarEventCounts = buckets.map((x) => x.eventCount);
            const isEmpty = CalendarEventCounts.length === 0;
            const TotalCalendarEventCount = isEmpty ? 0 : _.chain(CalendarEventCounts).sum().value();
            const AverageCalendarEventCount = isEmpty ? 0 : parseInt(_.mean(CalendarEventCounts).toFixed(1));
            const MaxCalendarEventCount = isEmpty ? 0 : parseInt(_.max(CalendarEventCounts).toFixed(1));
            const MinCalendarEventCount = isEmpty ? 0 : parseInt(_.min(CalendarEventCounts).toFixed(1));

            return {
                TotalCalendarEventCount,
                AverageCalendarEventCount,
                MaxCalendarEventCount,
                MinCalendarEventCount,
                isHourlyAverage: startDate.isSame(endDate, "day"),
            };
        },
        getXAxisData() {
            const { buckets, timeSeriesType } = this.graphData;
            log("getXAxisData.buckets", buckets);
            return buckets.map((item) => {
                if (timeSeriesType === "day") {
                    return moment(item.date).format("DD/MM");
                } else if (timeSeriesType === "hour") {
                    return moment(item.date).format("HH:mm");
                } else if (timeSeriesType === "week") {
                    return moment(item.date).format("YYYY-WW[week]");
                } else {
                    //monthly
                    return moment(item.date).format("MMM");
                }
            });
        },
        getChartDatasets() {
            const { buckets } = this.graphData;
            const data = buckets.map((bucket) => bucket.eventCount);
            return [
                {
                    label: this.$t("report.eventCount"),
                    data,
                    fill: true,
                    backgroundColor: "rgba(59, 130, 246, 0.12)",
                    borderColor: "rgba(59, 130, 246, 1)",
                    borderWidth: 2,
                    pointRadius: 3,
                    pointBackgroundColor: "rgba(59, 130, 246, 1)",
                    tension: 0.4,
                },
            ];
        },
        exportToExcel() {
            const { buckets, timeSeriesType } = this.graphData;
            log("exportToExcel.buckets", buckets);
            const data = buckets.map((bucket) => {
                const eventCount = bucket.eventCount;
                const date = {
                    hour: moment(bucket.date).format("DD/MM/YYYY HH:mm"),
                    day: moment(bucket.date).format("DD/MM/YYYY"),
                    month: moment(bucket.date).format("MM/YYYY"),
                    week: moment(bucket.date).format("YYYY-WW[week]"),
                }[timeSeriesType];
                return {
                    date,
                    eventCount,
                };
            });
            const stats = this.getStats();
            const excelData = [
                ...data,
                {
                    date: "Toplam etkinlik",
                    eventCount: stats.TotalCalendarEventCount,
                },
                {
                    date: "Ortalama etkinlik",
                    eventCount: stats.AverageCalendarEventCount,
                },
                {
                    date: "Maksimum etkinlik",
                    eventCount: stats.MaxCalendarEventCount,
                },
                {
                    date: "Minimum etkinlik",
                    eventCount: stats.MinCalendarEventCount,
                },
            ];
            expertToExcel(excelData, `calendar_event_count_timeseries.xlsx`, "Sheet 1");
        },
    },
    created() {
        const fullscreenCallback = () => {
            console.log("fullscreenCallback", fscreen.fullscreenElement);
            if (fscreen.fullscreenElement !== null) {
                this.fullScreen = true;
            } else {
                this.fullScreen = false;
            }
            this.$forceUpdate();
        };
        fscreen.addEventListener("fullscreenchange", fullscreenCallback);
        this.cancels.push(() => {
            fscreen.removeEventListener("fullscreenchange", fullscreenCallback);
        });
    },
    beforeDestroy() {
        this.cancels.forEach((cancel) => cancel());
    },
    mounted() {
        this.loadingInstance = this.$veLoading({
            target: document.querySelector(".calendar-events-chart"),
            name: "wave",
        });
        this.stats = this.getStats();
        this.fetch();
    },
    watch: {
        filters: {
            handler: function (val, oldVal) {
                this.fetch();
            },
            deep: true,
        },
        graphData: {
            handler: function (val, oldVal) {
                this.stats = this.getStats();
            },
            deep: true,
        },
        loading(val) {
            if (val) {
                this.loadingInstance.show();
            } else {
                this.loadingInstance.close();
            }
        },
    },
});
</script>
<style lang="scss">
.calendar-events-chart {
    .title {
        display: flex;
        gap: 4px;
        align-items: center;
        justify-content: center;
        margin: 0;

        h2 {
            text-align: center;
            font-weight: 700;
            font-size: 40px;
            margin: 0 !important;
        }
    }

    &.fullscreen {
        background-color: #fff;
        padding: 20px;
        overflow-y: scroll;

        .echarts-line-wrapper {
            height: calc(100vh - 200px);
        }
    }
}
</style>
<style scoped lang="scss">
.calendar-events-chart {
    display: flex;
    flex-direction: column;
    gap: 10px;

    .toolbar {
        display: flex;
        justify-content: space-between;

        .left {
            display: flex;

            .buttons {
                display: flex;
                border: 1px solid var(--color-border-default);

                .icon-button {
                    padding: 5px;
                    width: 40px;
                    height: 34px;
                    display: flex;
                    justify-content: center;
                    align-items: center;
                    border-right: 1px solid var(--color-border-default);
                    border-radius: unset !important;

                    &.selected {
                        background-color: var(--color-bg-info-low-hover);

                        i {
                            color: rgb(55, 162, 255);
                        }
                    }

                    &:hover {
                        background-color: var(--color-bg-neutral-pressed);
                    }
                }

                .icon-button:last-child {
                    border-right: none;
                }
            }
        }

        .right {
            display: flex;
            justify-content: flex-end;
        }
    }

    .stats {
        display: flex;
        flex-direction: row;
        gap: 10px;
        flex-wrap: wrap;
    }
}
</style>